import React, { useEffect, useState } from 'react';
import { clinicService } from '../services/clinicService';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

function Appointments() {
  const location = useLocation();
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDoctor, setFilterDoctor] = useState('');
  const [mobileSearch, setMobileSearch] = useState('');
  const [searchedPatient, setSearchedPatient] = useState(null);
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [bookedSlots, setBookedSlots] = useState([]);
  const { register, handleSubmit, reset, setValue, formState: { errors }, watch } = useForm();

  const watchedDate = watch('appointment_date');
  const watchedDoctor = watch('doctor_id');

  useEffect(() => {
    loadInitialData();
    // Check if navigation state has openBookModal or viewAppointment
    if (location.state?.openBookModal) {
      setShowModal(true);
    }
    if (location.state?.viewAppointment) {
      handleView(location.state.viewAppointment);
    }
  }, [location]);

  useEffect(() => {
    loadAppointments();
  }, [currentPage, searchTerm, filterStatus, filterDoctor]);

  // Load booked slots when date or doctor changes in booking modal
  useEffect(() => {
    if (showModal && watchedDate && watchedDoctor) {
      loadBookedSlots(watchedDate, watchedDoctor);
    }
  }, [showModal, watchedDate, watchedDoctor]);

  const loadBookedSlots = async (date, doctorId) => {
    try {
      const params = { 
        date: date,
        doctor_id: doctorId,
        per_page: 100
      };
      const response = await clinicService.getAppointments(params);
      setBookedSlots(response.data || []);
    } catch (error) {
      console.error('Failed to load booked slots', error);
    }
  };

  const loadInitialData = async () => {
    try {
      const [doctorsRes, patientsRes] = await Promise.all([
        clinicService.getDoctors({ per_page: 100 }),
        clinicService.getPatients({ per_page: 100 })
      ]);
      setDoctors(doctorsRes.data || []);
      setPatients(patientsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const params = { 
        page: currentPage, 
        search: searchTerm,
        status: filterStatus,
        doctor_id: filterDoctor
      };
      const response = await clinicService.getAppointments(params);
      setAppointments(response.data || []);
      setPagination(response.pagination || {});
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleMobileSearch = async () => {
    if (!mobileSearch || mobileSearch.length < 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      const response = await clinicService.getPatients({ search: mobileSearch });
      if (response.data && response.data.length > 0) {
        const patient = response.data[0];
        setSearchedPatient(patient);
        setValue('patient_id', patient.id);
        setShowPatientForm(false);
        toast.success(`Patient found: ${patient.name}`);
      } else {
        setSearchedPatient(null);
        setShowPatientForm(true);
        toast.info('Patient not found. Please fill in details to register.');
      }
    } catch (error) {
      toast.error('Failed to search patient');
    }
  };

  const handlePatientRegister = async (patientData) => {
    try {
      const newPatient = await clinicService.createPatient(patientData);
      setSearchedPatient(newPatient.data);
      setValue('patient_id', newPatient.data.id);
      setShowPatientForm(false);
      // Refresh patients list
      const patientsRes = await clinicService.getPatients({ per_page: 100 });
      setPatients(patientsRes.data || []);
      toast.success('Patient registered successfully!');
    } catch (error) {
      toast.error('Failed to register patient');
    }
  };

  const onSubmit = async (data) => {
    try {
      // Create proper datetime strings in YYYY-MM-DD HH:mm:ss format
      // This avoids timezone conversion issues
      const slotStart = `${data.appointment_date} ${data.slot_start}:00`;
      const slotEnd = `${data.appointment_date} ${data.slot_end}:00`;
      
      const appointmentData = {
        patient_id: data.patient_id,
        doctor_id: data.doctor_id,
        slot_start: slotStart,
        slot_end: slotEnd,
        notes: data.notes || ''
      };
      
      await clinicService.createAppointment(appointmentData);
      toast.success('Appointment booked successfully!');
      setShowModal(false);
      reset();
      setBookedSlots([]);
      loadAppointments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to book appointment');
    }
  };

  const handleCloseBookingModal = () => {
    setShowModal(false);
    reset();
    setMobileSearch('');
    setSearchedPatient(null);
    setShowPatientForm(false);
    setBookedSlots([]);
  };

  const handleViewBookedSlot = (appointment) => {
    handleView(appointment);
  };

  const formatTime = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleView = (appointment) => {
    setSelectedAppointment(appointment);
    setShowViewModal(true);
  };

  const handleEdit = (appointment) => {
    setSelectedAppointment(appointment);
    setValue('notes', appointment.notes || '');
    setValue('diagnosis', appointment.diagnosis || '');
    setValue('prescription', appointment.prescription || '');
    setValue('amount_paid', appointment.amount_paid || '');
    setValue('payment_status', appointment.payment_status || 'unpaid');
    setShowEditModal(true);
  };

  const handlePrintPrescription = (appointment) => {
    const printWindow = window.open('', '_blank');
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Prescription - ${appointment.appointment_number}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
          .header { text-align: center; border-bottom: 3px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { margin: 0; color: #2563eb; font-size: 2em; }
          .header p { margin: 5px 0; color: #666; }
          .rx-symbol { font-size: 3em; font-weight: bold; color: #2563eb; text-align: center; margin: 20px 0; }
          .section { margin: 20px 0; padding: 15px; background: #f9fafb; border-left: 4px solid #2563eb; }
          .section-title { font-weight: bold; color: #2563eb; font-size: 1.1em; margin-bottom: 10px; }
          .content { color: #333; white-space: pre-wrap; }
          .footer { margin-top: 50px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; color: #666; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 20px 0; }
          .info-item { padding: 10px; background: white; border: 1px solid #e5e7eb; border-radius: 4px; }
          .info-label { font-weight: bold; color: #666; font-size: 0.9em; }
          .info-value { color: #333; margin-top: 5px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🏥 Medical Prescription</h1>
          <p>Appointment #: ${appointment.appointment_number}</p>
          <p>Date: ${new Date(appointment.slot_start).toLocaleDateString('en-IN', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</p>
        </div>
        
        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">Patient Name</div>
            <div class="info-value">${appointment.patient?.name || 'N/A'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Patient Code</div>
            <div class="info-value">${appointment.patient?.patient_code || 'N/A'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Doctor</div>
            <div class="info-value">Dr. ${appointment.doctor?.name || 'N/A'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Specialization</div>
            <div class="info-value">${appointment.doctor?.specialization || 'General'}</div>
          </div>
        </div>

        ${appointment.diagnosis ? `
        <div class="section">
          <div class="section-title">📋 Diagnosis</div>
          <div class="content">${appointment.diagnosis}</div>
        </div>
        ` : ''}

        <div class="rx-symbol">℞</div>

        ${appointment.prescription ? `
        <div class="section">
          <div class="section-title">💊 Prescription</div>
          <div class="content">${appointment.prescription}</div>
        </div>
        ` : '<p style="text-align: center; color: #999;">No prescription provided</p>'}

        ${appointment.notes ? `
        <div class="section">
          <div class="section-title">📝 Additional Notes</div>
          <div class="content">${appointment.notes}</div>
        </div>
        ` : ''}

        <div class="footer">
          <p><strong>Doctor's Signature: _____________________</strong></p>
          <p>Dr. ${appointment.doctor?.name || ''}</p>
          <p style="font-size: 0.9em; margin-top: 20px;">This is a computer-generated prescription</p>
        </div>
        
        <script>
          window.onload = () => {
            window.print();
            window.onafterprint = () => window.close();
          }
        </script>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
  };

  const onEditSubmit = async (data) => {
    try {
      await clinicService.updateAppointment(selectedAppointment.id, {
        notes: data.notes,
        diagnosis: data.diagnosis,
        prescription: data.prescription,
        amount_paid: data.amount_paid,
        payment_status: data.payment_status
      });
      toast.success('Appointment updated successfully!');
      setShowEditModal(false);
      reset();
      setSelectedAppointment(null);
      loadAppointments();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    if (window.confirm(`Are you sure you want to mark this appointment as ${newStatus}?`)) {
      try {
        await clinicService.updateAppointmentStatus(id, newStatus);
        toast.success('Status updated successfully');
        loadAppointments();
      } catch (error) {
        toast.error('Failed to update status');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await clinicService.deleteAppointment(id);
        toast.success('Appointment deleted successfully');
        loadAppointments();
      } catch (error) {
        toast.error('Failed to delete appointment');
      }
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      booked: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDateTime = (datetime) => {
    const date = new Date(datetime);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  if (loading && appointments.length === 0) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">📅 Appointments</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold"
        >
          ➕ Book Appointment
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <input
          type="text"
          placeholder="🔍 Search by patient, doctor, or appointment number..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Status</option>
          <option value="booked">Booked</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="no_show">No Show</option>
        </select>
        <select
          value={filterDoctor}
          onChange={(e) => { setFilterDoctor(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Doctors</option>
          {doctors.map(doctor => (
            <option key={doctor.id} value={doctor.id}>{doctor.name}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Appt. No.</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Doctor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {appointments.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                  No appointments found. Book your first appointment!
                </td>
              </tr>
            ) : (
              appointments.map((appointment) => {
                const startTime = formatDateTime(appointment.slot_start);
                const endTime = formatDateTime(appointment.slot_end);
                return (
                  <tr key={appointment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-600">{appointment.appointment_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium">{appointment.patient?.name}</div>
                        <div className="text-sm text-gray-500">{appointment.patient?.mobile}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium">{appointment.doctor?.name}</div>
                        <div className="text-sm text-gray-500">{appointment.doctor?.specialization}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="font-medium">{startTime.date}</div>
                        <div className="text-sm text-gray-500">{startTime.time} - {endTime.time}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadge(appointment.status)}`}>
                        {appointment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleView(appointment)}
                        className="inline-flex items-center px-3 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg mr-2 font-medium transition"
                      >
                        👁️ View
                      </button>
                      <button
                        onClick={() => handleEdit(appointment)}
                        className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg mr-2 font-medium transition"
                      >
                        ✏️ Update
                      </button>
                      {(appointment.prescription || appointment.diagnosis) && (
                        <button
                          onClick={() => handlePrintPrescription(appointment)}
                          className="inline-flex items-center px-3 py-1.5 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-lg mr-2 font-medium transition"
                        >
                          🖨️ Print Rx
                        </button>
                      )}
                      {appointment.status === 'booked' && (
                        <button
                          onClick={() => handleStatusChange(appointment.id, 'confirmed')}
                          className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg mr-2 font-medium transition"
                        >
                          Confirm
                        </button>
                      )}
                      {['booked', 'confirmed'].includes(appointment.status) && (
                        <>
                          <button
                            onClick={() => handleStatusChange(appointment.id, 'completed')}
                            className="inline-flex items-center px-3 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg mr-2 font-medium transition"
                          >
                            Complete
                          </button>
                          <button
                            onClick={() => handleStatusChange(appointment.id, 'cancelled')}
                            className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg mr-2 font-medium transition"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} appointments
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 border rounded-lg bg-blue-50 text-blue-600 font-medium">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(pagination.last_page, p + 1))}
              disabled={currentPage === pagination.last_page}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Book Appointment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className="bg-white rounded-lg p-8 max-w-6xl w-full mx-4 max-h-[95vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Book New Appointment</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Booking Form - Left Side */}
              <div className="lg:col-span-2">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Mobile Search Section */}
                    <div className="col-span-2 bg-blue-50 p-4 rounded-lg">
                      <label className="block text-sm font-medium mb-2">Search Patient by Mobile</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={mobileSearch}
                          onChange={(e) => setMobileSearch(e.target.value)}
                          placeholder="Enter 10-digit mobile number"
                          maxLength="10"
                          className="flex-1 px-3 py-2 border rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={handleMobileSearch}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                        >
                          Search
                        </button>
                      </div>
                      {searchedPatient && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg">
                          <p className="text-sm font-medium text-green-800">✓ Patient Found:</p>
                          <p className="text-sm">{searchedPatient.name} - {searchedPatient.patient_code}</p>
                        </div>
                      )}
                    </div>

                    {/* Patient Registration Form (if not found) */}
                    {showPatientForm && (
                      <div className="col-span-2 bg-yellow-50 p-4 rounded-lg space-y-3">
                        <h3 className="font-semibold text-yellow-800">Register New Patient</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1">Name *</label>
                            <input
                              type="text"
                              id="new_patient_name"
                              placeholder="Patient name"
                              className="w-full px-2 py-1.5 text-sm border rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Mobile *</label>
                            <input
                              type="text"
                              id="new_patient_mobile"
                              value={mobileSearch}
                              readOnly
                              className="w-full px-2 py-1.5 text-sm border rounded bg-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Email</label>
                            <input
                              type="email"
                              id="new_patient_email"
                              placeholder="Email address"
                              className="w-full px-2 py-1.5 text-sm border rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">Gender *</label>
                            <select id="new_patient_gender" className="w-full px-2 py-1.5 text-sm border rounded">
                              <option value="">Select</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const name = document.getElementById('new_patient_name').value;
                            const mobile = document.getElementById('new_patient_mobile').value;
                            const email = document.getElementById('new_patient_email').value;
                            const gender = document.getElementById('new_patient_gender').value;
                            
                            if (!name || !mobile || !gender) {
                              toast.error('Please fill required fields');
                              return;
                            }
                            
                            handlePatientRegister({ name, mobile, email, gender });
                          }}
                          className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                        >
                          Register & Continue
                        </button>
                      </div>
                    )}

                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Patient *</label>
                      <select {...register('patient_id', { required: 'Patient is required' })} className="w-full px-3 py-2 border rounded-lg">
                        <option value="">Select Patient</option>
                        {patients.map(patient => (
                          <option key={patient.id} value={patient.id}>
                            {patient.name} - {patient.patient_code} ({patient.mobile})
                          </option>
                        ))}
                      </select>
                      {errors.patient_id && <span className="text-red-500 text-xs">{errors.patient_id.message}</span>}
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Doctor *</label>
                      <select {...register('doctor_id', { required: 'Doctor is required' })} className="w-full px-3 py-2 border rounded-lg">
                        <option value="">Select Doctor</option>
                        {doctors.map(doctor => (
                          <option key={doctor.id} value={doctor.id}>
                            {doctor.name} - {doctor.specialization}
                          </option>
                        ))}
                      </select>
                      {errors.doctor_id && <span className="text-red-500 text-xs">{errors.doctor_id.message}</span>}
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Appointment Date *</label>
                      <input 
                        type="date" 
                        {...register('appointment_date', { required: 'Date is required' })} 
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border rounded-lg" 
                      />
                      {errors.appointment_date && <span className="text-red-500 text-xs">{errors.appointment_date.message}</span>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Start Time *</label>
                      <input 
                        type="time" 
                        {...register('slot_start', { required: 'Start time is required' })} 
                        className="w-full px-3 py-2 border rounded-lg" 
                      />
                      {errors.slot_start && <span className="text-red-500 text-xs">{errors.slot_start.message}</span>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">End Time *</label>
                      <input 
                        type="time" 
                        {...register('slot_end', { required: 'End time is required' })} 
                        className="w-full px-3 py-2 border rounded-lg" 
                      />
                      {errors.slot_end && <span className="text-red-500 text-xs">{errors.slot_end.message}</span>}
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Notes</label>
                      <textarea {...register('notes')} className="w-full px-3 py-2 border rounded-lg" rows="3" placeholder="Chief complaint, symptoms, etc." />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold">
                      Book Appointment
                    </button>
                    <button type="button" onClick={handleCloseBookingModal} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>

              {/* Booked Appointments - Right Side */}
              <div className="lg:col-span-1 bg-gray-50 rounded-lg p-4 max-h-[600px] overflow-y-auto">
                <h3 className="font-bold text-lg mb-4 sticky top-0 bg-gray-50 pb-2">
                  📋 Booked Appointments
                </h3>
                
                {!watchedDate || !watchedDoctor ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">Select date and doctor to see booked appointments</p>
                  </div>
                ) : bookedSlots.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No appointments booked for this date</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {bookedSlots
                      .sort((a, b) => new Date(a.slot_start) - new Date(b.slot_start))
                      .map((slot) => (
                      <div
                        key={slot.id}
                        onClick={() => handleViewBookedSlot(slot)}
                        className={`p-3 rounded-lg border-l-4 cursor-pointer transition ${
                          slot.status === 'booked' ? 'bg-yellow-50 border-yellow-500 hover:bg-yellow-100' :
                          slot.status === 'confirmed' ? 'bg-blue-50 border-blue-500 hover:bg-blue-100' :
                          slot.status === 'completed' ? 'bg-green-50 border-green-500 hover:bg-green-100' :
                          'bg-gray-100 border-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-semibold text-sm">
                            {formatTime(slot.slot_start)} - {formatTime(slot.slot_end)}
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusBadge(slot.status)}`}>
                            {slot.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-700">
                          <div className="font-medium">{slot.patient?.name}</div>
                          <div className="text-gray-500">{slot.appointment_number}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {bookedSlots.length > 0 && (
                  <div className="mt-4 pt-4 border-t text-xs text-gray-500">
                    <p>💡 Click on any appointment to view details</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Appointment Modal */}
      {showViewModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Appointment Details</h2>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Appointment Number</label>
                  <p className="text-lg font-semibold text-blue-600">{selectedAppointment.appointment_number}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadge(selectedAppointment.status)}`}>
                    {selectedAppointment.status}
                  </span>
                </div>
                <div className="col-span-2 border-t pt-4">
                  <h3 className="font-semibold text-lg mb-2">Patient Information</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Name</label>
                      <p className="text-lg">{selectedAppointment.patient?.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Patient Code</label>
                      <p className="text-lg">{selectedAppointment.patient?.patient_code}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Mobile</label>
                      <p className="text-lg">{selectedAppointment.patient?.mobile}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Email</label>
                      <p className="text-lg">{selectedAppointment.patient?.email || '-'}</p>
                    </div>
                  </div>
                </div>
                <div className="col-span-2 border-t pt-4">
                  <h3 className="font-semibold text-lg mb-2">Doctor Information</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Name</label>
                      <p className="text-lg">{selectedAppointment.doctor?.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Specialization</label>
                      <p className="text-lg">{selectedAppointment.doctor?.specialization}</p>
                    </div>
                  </div>
                </div>
                <div className="col-span-2 border-t pt-4">
                  <h3 className="font-semibold text-lg mb-2">Appointment Schedule</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Date</label>
                      <p className="text-lg">{formatDateTime(selectedAppointment.slot_start).date}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Time</label>
                      <p className="text-lg">{formatDateTime(selectedAppointment.slot_start).time} - {formatDateTime(selectedAppointment.slot_end).time}</p>
                    </div>
                  </div>
                </div>
                {selectedAppointment.notes && (
                  <div className="col-span-2 border-t pt-4">
                    <label className="block text-sm font-medium text-gray-500 mb-2">Notes</label>
                    <p className="text-lg bg-gray-50 p-3 rounded">{selectedAppointment.notes}</p>
                  </div>
                )}
                {selectedAppointment.prescription && (
                  <div className="col-span-2 border-t pt-4">
                    <label className="block text-sm font-medium text-gray-500 mb-2">Prescription</label>
                    <p className="text-lg bg-blue-50 p-3 rounded">{selectedAppointment.prescription}</p>
                  </div>
                )}
                {selectedAppointment.diagnosis && (
                  <div className="col-span-2 border-t pt-4">
                    <label className="block text-sm font-medium text-gray-500 mb-2">Diagnosis</label>
                    <p className="text-lg bg-green-50 p-3 rounded">{selectedAppointment.diagnosis}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowViewModal(false)} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Appointment Modal */}
      {showEditModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Update Appointment - {selectedAppointment.appointment_number}</h2>
              <button onClick={() => { setShowEditModal(false); reset(); }} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-sm text-gray-600">Patient</p>
                  <p className="font-semibold">{selectedAppointment.patient?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Doctor</p>
                  <p className="font-semibold">{selectedAppointment.doctor?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date & Time</p>
                  <p className="font-semibold">{formatDateTime(selectedAppointment.slot_start).date} at {formatDateTime(selectedAppointment.slot_start).time}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadge(selectedAppointment.status)}`}>
                    {selectedAppointment.status}
                  </span>
                </div>
              </div>
            </div>
            <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Chief Complaint / Notes</label>
                <textarea 
                  {...register('notes')} 
                  className="w-full px-3 py-2 border rounded-lg" 
                  rows="3" 
                  placeholder="Patient's chief complaint, symptoms, reason for visit..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Diagnosis</label>
                <textarea 
                  {...register('diagnosis')} 
                  className="w-full px-3 py-2 border rounded-lg" 
                  rows="3" 
                  placeholder="Medical diagnosis based on examination and tests..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Prescription</label>
                <textarea 
                  {...register('prescription')} 
                  className="w-full px-3 py-2 border rounded-lg" 
                  rows="5" 
                  placeholder="Medications, dosage, instructions&#10;Example:&#10;1. Tab Paracetamol 500mg - 1-0-1 after food for 3 days&#10;2. Syrup Cough - 2 tsp thrice daily for 5 days"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Amount Paid (₹)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    {...register('amount_paid')} 
                    className="w-full px-3 py-2 border rounded-lg" 
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Consultation Fee: ₹{selectedAppointment.doctor?.consultation_fee}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Status</label>
                  <select {...register('payment_status')} className="w-full px-3 py-2 border rounded-lg">
                    <option value="unpaid">Unpaid</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold">
                  Save Changes
                </button>
                <button type="button" onClick={() => { setShowEditModal(false); reset(); }} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Appointments;
